import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreditCard,
  Landmark,
  Link as LinkIcon,
  Mail,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Download,
} from 'lucide-react';
import { fetchPaiementInfos, choisirModePaiement, createStripeSession, fetchFactureStatus } from '../services/api';

const modes = [
  {
    id: 'carte',
    label: 'Carte bancaire en ligne',
    description: 'Paiement sécurisé par Stripe (carte ou wallet).',
    icon: CreditCard,
  },
  {
    id: 'virement',
    label: 'Virement bancaire',
    description: 'Virement classique ou instantané sur le compte de l\'association.',
    icon: Landmark,
  },
  {
    id: 'cheque',
    label: 'Chèque',
    description: 'Chèque à l\'ordre du Comité des Fêtes, envoyé par courrier.',
    icon: Mail,
  },
];

/**
 * L'icône de chaque mode.
 *
 * La liste affichée vient du serveur — c'est lui qui sait si l'association a
 * posé un lien de paiement à elle et comment elle l'appelle. Les libellés
 * ci-dessus ne servent plus que de secours, le temps que la requête revienne.
 */
const ICONES_MODE: Record<string, any> = {
  carte: CreditCard,
  virement: Landmark,
  cheque: Mail,
  lien: LinkIcon,
};

export default function PaiementPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(null);
  const [stripeMessage] = useState<string | null>(searchParams.get('stripe') === 'success' ? 'Paiement Stripe confirmé.' : null);

  const infosQuery = useQuery({
    queryKey: ['paiement-infos', token],
    queryFn: () => fetchPaiementInfos(token!),
    enabled: !!token,
  });

  const factureQuery = useQuery({
    queryKey: ['facture', token],
    queryFn: () => fetchFactureStatus(token!),
    enabled: !!token,
    refetchInterval: searchParams.get('stripe') === 'success' ? 3000 : false,
  });

  const choixMutation = useMutation({
    mutationFn: () => choisirModePaiement(token!, selected as any),
    onSuccess: () => factureQuery.refetch(),
  });

  const stripeSessionMutation = useMutation({
    mutationFn: () => createStripeSession(token!),
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });

  /*
   * L'ouverture de la page Stripe, une fois la carte choisie.
   *
   * `stripeSessionMutation` est volontairement hors des dépendances, et c'est
   * ce que le contrôle réclamerait à tort : l'objet de mutation est recréé à
   * chaque rendu, l'y mettre relancerait l'effet à chaque rendu — donc
   * `mutate()` en boucle, donc une session de paiement ouverte après l'autre.
   *
   * `mutate` est stable de son côté ; ce qui doit déclencher, et déclenche
   * seul, c'est le succès du choix de mode.
   */
  useEffect(() => {
    if (choixMutation.isSuccess && choixMutation.data?.facture?.modePaiement === 'carte' && choixMutation.data?.facture?.statut !== 'payee') {
      stripeSessionMutation.mutate();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [choixMutation.isSuccess, choixMutation.data]);

  const facture = choixMutation.data?.facture ?? factureQuery.data?.facture;
  const isPaid = facture?.statut === 'payee';
  const isWaitingPayment = facture?.statut === 'en_attente_paiement';

  const souscription = infosQuery.data?.souscription;
  const modesActifs = infosQuery.data?.modesPaiement ?? [];

  if (infosQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (infosQuery.isError || !infosQuery.data?.success) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Lien de paiement invalide</p>
            <p className="text-sm mt-1">
              Ce devis n'est pas valide ou le lien a expiré. Contactez le bureau du comité.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isModeActif = (id: string) => modesActifs.find((m: any) => m.id === id)?.actif ?? false;

  // La liste vient du serveur dès qu'elle est là : lui seul sait si
  // l'association a posé un lien de paiement à elle, et sous quel nom.
  const modesAffiches: { id: string; label: string; description: string; icon: any }[] =
    modesActifs.length > 0
      ? modesActifs.map((m: any) => ({
          id: m.id,
          label: m.label,
          description: m.description,
          icon: ICONES_MODE[m.id] ?? LinkIcon,
        }))
      : modes;

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Paiement du sponsoring</h1>
        <p className="mt-2 text-sm lg:text-base text-gray-600 dark:text-gray-400">
          Merci d'avoir validé votre devis. Choisissez ci-dessous le mode de paiement qui vous
          convient.
        </p>
      </div>

      <div className="card bg-primary-50 border-primary-100 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Sponsor</p>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{souscription?.nomSponsor}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{souscription?.emailSponsor}</p>
          </div>
          <div className="text-left lg:text-right">
            <p className="text-sm text-gray-600 dark:text-gray-400">Montant à régler</p>
            <p className="text-2xl font-bold text-primary-700">{souscription?.montantTotal} €</p>
          </div>
        </div>
      </div>

      {stripeSessionMutation.isPending ? (
        <div className="card p-8 flex flex-col items-center gap-3 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <p className="text-gray-700 dark:text-gray-300">Redirection vers Stripe en cours…</p>
        </div>
      ) : facture ? (
        <div className="space-y-5">
          <div className={`card lg:p-6 ${isPaid ? 'bg-green-50 border-green-200 text-green-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="w-5 h-5" />
              {isPaid ? 'Paiement confirmé' : (stripeMessage ?? 'Facture générée')}
            </div>
            <p className="mt-2 text-sm lg:text-base">
              {isPaid
                ? 'Votre règlement a été pris en compte. Votre facture acquittée est disponible ci-dessous.'
                : 'Votre facture a été générée. Vous pouvez la télécharger ci-dessous.'}
            </p>
          </div>

          <div className="card lg:p-6 space-y-4">
            <h2 className="font-semibold lg:text-lg">Récapitulatif</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Facture <strong>n° {facture.numero}</strong>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Mode : <strong>{modesAffiches.find((m) => m.id === facture.modePaiement)?.label ?? facture.modePaiement}</strong>
            </p>
            {isPaid && facture.referencePaiement && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Référence : <strong>{facture.referencePaiement}</strong>
              </p>
            )}

            {facture.pdfUrl && (
              <a
                href={facture.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 btn-primary"
              >
                <Download className="w-4 h-4" />
                Télécharger la facture
              </a>
            )}

            {choixMutation.data?.instructions && (
              <Instructions mode={facture.modePaiement} instructions={choixMutation.data.instructions} />
            )}

            {facture.modePaiement === 'carte' && !isPaid && isWaitingPayment && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Le paiement par carte sera activé dès que les clés Stripe seront configurées par le bureau.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {modesAffiches.map((mode) => {
              const actif = isModeActif(mode.id);
              const Icon = mode.icon;
              return (
                <button
                  key={mode.id}
                  onClick={() => actif && setSelected(mode.id)}
                  disabled={!actif}
                  className={`card text-left p-4 lg:p-5 transition border-2 ${
                    selected === mode.id
                      ? 'border-primary-600 bg-primary-50'
                      : actif
                        ? 'border-transparent hover:border-primary-300'
                        : 'opacity-60 cursor-not-allowed border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <Icon className="w-6 h-6 text-primary-700" />
                    {!actif && (
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded dark:text-gray-300">
                        Indisponible
                      </span>
                    )}
                  </div>
                  <p className="mt-3 font-semibold text-gray-900 dark:text-gray-100">{mode.label}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{mode.description}</p>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => choixMutation.mutate()}
            disabled={!selected || choixMutation.isPending}
            className="btn-primary w-full lg:w-auto lg:px-8 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {choixMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {choixMutation.isPending ? 'Génération de la facture…' : 'Valider ce mode de paiement'}
          </button>

          {choixMutation.isError && (
            <p className="text-sm text-red-600">
              Une erreur est survenue. Veuillez vérifier le mode sélectionné et réessayer.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Instructions({ mode, instructions }: { mode: string; instructions: any }) {
  // Le lien que l'association tient elle-même. L'avertissement n'est pas une
  // précaution juridique : rien ne revient de ce lien, et laisser croire au
  // payeur que son règlement est enregistré serait la pire des politesses —
  // il recevrait une relance quinze jours plus tard.
  if (mode === 'lien' && instructions?.url) {
    return (
      <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 p-4 space-y-3 text-sm">
        <p className="font-semibold text-gray-900 dark:text-gray-100">{instructions.label}</p>
        {instructions.aide && (
          <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">{instructions.aide}</p>
        )}
        <a
          href={instructions.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 btn-primary"
        >
          <LinkIcon className="w-4 h-4" />
          Ouvrir la page de règlement
        </a>
        <p className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-200">
          {instructions.avertissement}
        </p>
      </div>
    );
  }

  if (mode === 'virement') {
    return (
      <div className="space-y-2 text-sm text-gray-700 bg-gray-50 p-4 rounded-lg dark:text-gray-300 dark:bg-gray-800">
        <p className="font-semibold">{instructions.titre}</p>
        <p>IBAN : <span className="font-mono bg-white px-1 rounded border">{instructions.iban}</span></p>
        {instructions.bic && <p>BIC : <span className="font-mono bg-white px-1 rounded border">{instructions.bic}</span></p>}
        <p>Titulaire : {instructions.titulaire}</p>
        <p>Référence : {instructions.reference}</p>
        <p className="text-gray-500 dark:text-gray-400">Délai : {instructions.delai}</p>
      </div>
    );
  }

  if (mode === 'cheque') {
    return (
      <div className="space-y-2 text-sm text-gray-700 bg-gray-50 p-4 rounded-lg dark:text-gray-300 dark:bg-gray-800">
        <p className="font-semibold">{instructions.titre}</p>
        <p>Ordre : {instructions.ordre}</p>
        <p>Adresse d'envoi :</p>
        <p className="whitespace-pre-line font-mono bg-white p-2 rounded border">{instructions.adresse}</p>
        <p className="text-gray-500 dark:text-gray-400">Délai : {instructions.delai}</p>
      </div>
    );
  }

  return null;
}
